import InfiniteScroll from 'react-infinite-scroll-component';
import { Role, rolesManipulator } from 'auth';
import { Fragment, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import { useGetRegisterIdParam } from '@/modules/common/hooks/useGetRegisterIdParam.ts';
import { useRegisterPermissionsQuery } from '@/modules/common/queries/permissions.ts';
import { BankInfoVerificationRequestItem } from '@/modules/issuer-dashboard/components/holder-create/BankInfoVerificationRequestItem.tsx';
import { HolderEditItem } from '@/modules/issuer-dashboard/components/holder-create/HolderEditItem.tsx';
import { PendingHolderRequestItem } from '@/modules/issuer-dashboard/components/holder-create/PendingHolderRequestItem.tsx';
import {
  HOLDERS_TAB_NAME,
  HoldersTabNames,
} from '@/modules/issuer-dashboard/pages/IssuerHolders.tsx';
import { useIssuerEntitiesQuery } from '@/modules/issuer-dashboard/queries/entities.ts';
import { useIssuerGetInvestorsEditListQuery } from '@/modules/issuer-dashboard/queries/holder-create.tsx';
import { useRegisterShareholdersQuery } from '@/modules/issuer-dashboard/queries/shareholders.tsx';
import { issuerDashboardPaths } from '@/modules/issuer-dashboard/routes.tsx';
import { EmptyListState, Spinner } from 'ui';
import {
  EntitiesPaymentInfoStatus,
  EntityStatus,
  EntityWithDocumentResponse,
  InvestorEditsStatus,
  ShareholderExtendResponse,
} from 'api';

const activeInvestorEditStatuses = [
  InvestorEditsStatus.Pending,
  InvestorEditsStatus.InProgress,
].toString();
const pendingHolderStatuses = [
  EntityStatus.EntityAwaitingApproval,
  EntityStatus.EntityUnsuccessfull,
  EntityStatus.PermanentlyRejected,
].toString();

const SectionHeader = ({ title, count }: { title: string; count: number }) => (
  <div className="flex items-center justify-between">
    <h3 className="text-lg font-medium">{title}</h3>
    <span className="text-sm text-gray-500">{count}</span>
  </div>
);

interface EditRequestsTabProps {
  tab: HoldersTabNames;
  counts: {
    pendingHolders: number;
    editRequests: number;
    bankInfoVerificationRequests: number;
  };
}

const EditRequestsTab = ({ tab, counts }: EditRequestsTabProps) => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const registerId = useGetRegisterIdParam();
  const isIssuerAdmin = rolesManipulator.current_user_role === Role.SR_ISSUER_ADMIN;
  const activeRegisterId = Number(registerId || 0);

  const { data: dataRegisterPermissions, isLoading: isLoadingRegisterPermissions } =
    useRegisterPermissionsQuery();
  const {
    data: dataPendingHolders,
    isLoading: isLoadingPendingHolders,
    fetchNextPage: fetchMorePendingHolders,
    hasNextPage: hasMorePendingHolders,
  } = useIssuerEntitiesQuery(
    {
      page: 1,
      page_size: 16,
      status: pendingHolderStatuses as unknown as EntityStatus,
      apply_register_id: activeRegisterId || undefined,
    },
    !isIssuerAdmin,
  );
  const {
    data: dataInvestorsEditList,
    isLoading: isLoadingInvestorEdits,
    fetchNextPage: fetchMoreInvestorEdits,
    hasNextPage: hasMoreInvestorEdits,
  } = useIssuerGetInvestorsEditListQuery({
    page: 1,
    page_size: 16,
    status: activeInvestorEditStatuses,
  });
  const { data: dataRegisterShareholders, isLoading: isLoadingRegisterShareholders } =
    useRegisterShareholdersQuery(
      { register_id: activeRegisterId },
      !registerId || !dataRegisterPermissions?.holder_details_permission,
    );

  useEffect(() => {
    const shouldBlockByPermissions =
      !dataRegisterPermissions?.holder_details_permission && !isLoadingRegisterPermissions;
    const shouldBlockPendingHolders =
      tab === HoldersTabNames.PendingHolders && !isIssuerAdmin && !isLoadingRegisterPermissions;

    if (shouldBlockByPermissions || shouldBlockPendingHolders) {
      const nextParams = new URLSearchParams(params);
      nextParams.set(HOLDERS_TAB_NAME, HoldersTabNames.Overview);
      navigate(
        {
          pathname: issuerDashboardPaths.static.holders,
          search: nextParams.toString(),
        },
        {
          replace: false,
        },
      );
    }
  }, [
    dataRegisterPermissions?.holder_details_permission,
    isIssuerAdmin,
    isLoadingRegisterPermissions,
    navigate,
    params,
    tab,
  ]);

  const pendingBankInfoRequests = useMemo(
    () =>
      (dataRegisterShareholders?.shareholders || [])
        .filter(
          (shareholder: ShareholderExtendResponse) =>
            shareholder.entity?.payment_info_status === EntitiesPaymentInfoStatus.Unverified &&
            shareholder.entity?.payment_info_requested_at,
        )
        .sort((a: ShareholderExtendResponse, b: ShareholderExtendResponse) =>
          (b.entity?.payment_info_requested_at || '').localeCompare(
            a.entity?.payment_info_requested_at || '',
          ),
        ),
    [dataRegisterShareholders],
  );

  const pendingHoldersCount = isIssuerAdmin ? counts.pendingHolders : 0;
  const pendingInvestorEditsCount = counts.editRequests;
  const pendingBankInfoRequestsCount = counts.bankInfoVerificationRequests;
  const pendingHolders = useMemo(
    () => dataPendingHolders?.pages?.flatMap(page => page.data) ?? [],
    [dataPendingHolders?.pages],
  );
  const selectedTabCountByTab = {
    [HoldersTabNames.PendingHolders]: pendingHoldersCount,
    [HoldersTabNames.EditRequests]: pendingInvestorEditsCount,
    [HoldersTabNames.BankInfoVerificationRequests]: pendingBankInfoRequestsCount,
    [HoldersTabNames.Overview]: 0,
  };
  const isLoading =
    isLoadingPendingHolders || isLoadingInvestorEdits || isLoadingRegisterShareholders;

  const renderEmptyState = (title: string, description: string) => (
    <div className="w-full flex flex-col items-start gap-4">
      <EmptyListState title={title} description={description} isLoading={false} itemsCount={0} />
    </div>
  );

  if (!isLoading && selectedTabCountByTab[tab] === 0) {
    if (tab === HoldersTabNames.PendingHolders) {
      return renderEmptyState(
        'No pending holders right now',
        'New holders awaiting issuer-admin review will appear here.',
      );
    }

    if (tab === HoldersTabNames.EditRequests) {
      return renderEmptyState(
        'No edit requests right now',
        'Created holder requests and holder change requests will appear here.',
      );
    }

    return renderEmptyState(
      'No bank info verification requests right now',
      'Bank details awaiting verification will appear here.',
    );
  }

  return (
    <div className="w-full flex flex-col items-start gap-8">
      {isLoading && (
        <div className="w-full h-full min-h-[300px] flex items-center justify-center">
          <Spinner size="lg" />
        </div>
      )}

      {!isLoading && tab === HoldersTabNames.PendingHolders && isIssuerAdmin && (
        <div className="w-full flex flex-col gap-2">
          {pendingHoldersCount ? (
            <>
              <SectionHeader title="Pending holders" count={pendingHoldersCount} />
              <InfiniteScroll
                next={fetchMorePendingHolders}
                dataLength={dataPendingHolders?.pages.length ?? 0}
                hasMore={hasMorePendingHolders || false}
                style={{ overflow: 'unset' }}
                loader={
                  <div className="flex justify-center items-center h-16 w-full">
                    <Spinner size="md" variant="primary" />
                  </div>
                }
                scrollableTarget="scrollable"
              >
                <div className="w-full flex flex-col gap-2">
                  {pendingHolders.map((item: EntityWithDocumentResponse) => (
                    <PendingHolderRequestItem key={item.id} item={item} />
                  ))}
                </div>
              </InfiniteScroll>
            </>
          ) : (
            <span className="text-sm text-gray-500">No pending holder approvals.</span>
          )}
        </div>
      )}

      {!isLoading && tab === HoldersTabNames.EditRequests && (
        <div className="w-full flex flex-col gap-2">
          <SectionHeader
            title="Created holders and change requests"
            count={pendingInvestorEditsCount}
          />
          {pendingInvestorEditsCount ? (
            <>
              <InfiniteScroll
                next={fetchMoreInvestorEdits}
                dataLength={dataInvestorsEditList?.pages.length ?? 0}
                hasMore={hasMoreInvestorEdits || false}
                style={{ overflow: 'unset' }}
                loader={
                  <div className="flex justify-center items-center h-16 w-full">
                    <Spinner size="md" variant="primary" />
                  </div>
                }
                scrollableTarget="scrollable"
              >
                <div className="w-full flex flex-col gap-2">
                  {dataInvestorsEditList?.pages?.map(({ data }, index) => (
                    <Fragment key={index}>
                      {data.map(item => (
                        <HolderEditItem key={item.id} item={item} />
                      ))}
                    </Fragment>
                  ))}
                </div>
              </InfiniteScroll>
            </>
          ) : (
            <span className="text-sm text-gray-500">
              No created holder or holder change requests.
            </span>
          )}
        </div>
      )}

      {!isLoading && tab === HoldersTabNames.BankInfoVerificationRequests && (
        <div className="w-full flex flex-col gap-2">
          <SectionHeader
            title="Bank info verification requests"
            count={pendingBankInfoRequestsCount}
          />
          {pendingBankInfoRequestsCount ? (
            <div className="w-full flex flex-col gap-2">
              {pendingBankInfoRequests.map((shareholder: ShareholderExtendResponse) => (
                <BankInfoVerificationRequestItem
                  key={`bank-info-${shareholder.id}`}
                  shareholder={shareholder}
                />
              ))}
            </div>
          ) : (
            <span className="text-sm text-gray-500">No bank info verification requests.</span>
          )}
        </div>
      )}
    </div>
  );
};

export default withPrivateAccess(EditRequestsTab, [Role.SR_ISSUER, Role.SR_ISSUER_ADMIN]);
